import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Check, X, Save, Edit3 } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';

/**
 * Live Page Audit Widget
 * Floating button on every page to track pending updates/polish items
 */
export default function PageAuditWidget() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageStatus, setPageStatus] = useState(null);

  const currentPage = location.pathname;

  const loadPageAudit = useCallback(async () => {
    try {
      const response = await api.get(`/api/page-audit?page=${encodeURIComponent(currentPage)}`);
      setPageStatus(response.data);
      setNotes(response.data.notes || []);
    } catch (error) {
      // Page audit not found, initialize empty
      setPageStatus({ page: currentPage, status: 'ok', notes: [] });
      setNotes([]);
    }
  }, [currentPage]);

  useEffect(() => {
    loadPageAudit();
  }, [loadPageAudit]);

  // Listen for CoPilot trigger
  useEffect(() => {
    const handleOpenAudit = () => setIsOpen(true);
    window.addEventListener('open-page-audit', handleOpenAudit);
    return () => window.removeEventListener('open-page-audit', handleOpenAudit);
  }, []);

  const addNote = async () => {
    if (!newNote.trim()) return;
    
    setLoading(true);
    try {
      await api.post('/api/page-audit/note', {
        page: currentPage,
        note: newNote.trim(),
        priority: 'medium'
      });
      
      toast.success('Note added');
      setNewNote('');
      loadPageAudit();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setLoading(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/api/page-audit/note/${noteId}`);
      toast.success('Note removed');
      loadPageAudit();
    } catch (error) {
      toast.error('Failed to remove note');
    }
  };

  const toggleNoteStatus = async (noteId, currentStatus) => {
    try {
      await api.put(`/api/page-audit/note/${noteId}`, {
        status: currentStatus === 'pending' ? 'done' : 'pending'
      });
      loadPageAudit();
    } catch (error) {
      toast.error('Failed to update note');
    }
  };

  const getStatusColor = () => {
    const pendingCount = notes.filter(n => n.status === 'pending').length;
    if (pendingCount === 0) return 'from-green-500 to-emerald-500';
    if (pendingCount <= 2) return 'from-amber-500 to-orange-500';
    return 'from-red-500 to-rose-500';
  };

  const pendingCount = notes.filter(n => n.status === 'pending').length;

  return (
    <>
      {/* Audit Panel - Opens from CoPilot */}
      {isOpen && (
        <div className="fixed bottom-24 right-24 z-[70] w-96 bg-gray-900/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-b border-white/10 px-6 py-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Edit3 size={20} className="text-purple-400" />
                <h3 className="text-lg font-bold text-white">Page Audit</h3>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-white/10 rounded-lg transition text-gray-400 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <p className="text-xs text-gray-400 truncate">{currentPage}</p>
          </div>

          {/* Notes List */}
          <div className="p-4 max-h-80 overflow-y-auto space-y-3">
            {notes.length === 0 ? (
              <div className="text-center py-8">
                <Check size={48} className="text-green-400 mx-auto mb-3 opacity-50" />
                <p className="text-sm text-gray-500">No pending items</p>
                <p className="text-xs text-gray-600 mt-1">This page is up to date!</p>
              </div>
            ) : (
              notes.map((note, idx) => (
                <div
                  key={note.id || idx}
                  className={`p-3 rounded-xl border transition-all ${
                    note.status === 'done'
                      ? 'bg-green-500/10 border-green-500/30 opacity-60'
                      : 'bg-gray-800/50 border-white/5'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <button
                      onClick={() => toggleNoteStatus(note.id, note.status)}
                      className="flex-shrink-0 mt-0.5"
                    >
                      {note.status === 'done' ? (
                        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                          <Check size={12} className="text-white" />
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-600 hover:border-purple-400 transition"></div>
                      )}
                    </button>
                    
                    <p className={`flex-1 text-sm mx-3 ${
                      note.status === 'done' ? 'line-through text-gray-500' : 'text-gray-300'
                    }`}>
                      {note.text || note.note}
                    </p>
                    
                    <button
                      onClick={() => deleteNote(note.id)}
                      className="flex-shrink-0 p-1 hover:bg-red-500/20 rounded-lg transition text-gray-500 hover:text-red-400"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  
                  {note.created_at && (
                    <p className="text-xs text-gray-600 ml-8">
                      {new Date(note.created_at).toLocaleString()}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Add Note Form */}
          <div className="border-t border-white/10 p-4">
            <div className="flex space-x-2">
              <input
                type="text"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addNote()}
                placeholder="Add polish item or note..."
                className="flex-1 px-4 py-2.5 bg-gray-800/50 border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition"
                disabled={loading}
              />
              <button
                onClick={addNote}
                disabled={loading || !newNote.trim()}
                className="px-4 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Save size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
